import { useLazyQuery } from '@apollo/client/react';
import { logError } from 'fergy-core-react-logging';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
	type PricedOptionSelectionFieldsFragment,
	type ValidateKeyCodeQuery,
	type ValidateKeyCodeQueryVariables
} from '../../__generated__/graphql-client-types';
import { VALIDATE_KEYCODE } from '../../queries/product/priced-options/priced-options.queries';

/** `validating` must stay distinct from `invalid`, or a pending value renders as rejected the instant it is typed. */
export type KeycodeStatus = 'validating' | 'valid' | 'invalid';

export type KeycodeTextInput = {
	value: string;
	status: KeycodeStatus;
};

/** Internal only: carries the option fragment so `textInputOptions` can be rebuilt from state. */
type TextInputEntry = KeycodeTextInput & {
	option: PricedOptionSelectionFieldsFragment;
};

type KeycodeValidationHook = {
	textInputs: Record<string, KeycodeTextInput>;
	/** Only the values the service has confirmed, shaped as priced options for cart consumers. */
	textInputOptions: PricedOptionSelectionFieldsFragment[];
	/** Records a value and validates it. An empty value drops the entry without issuing a request. */
	handleSetTextInput: (option: PricedOptionSelectionFieldsFragment, value: string) => void;
};

/**
 * Owns keycode values and their verdicts for a variant. There is no local format check - `validateKeyCode`
 * is the sole authority on what counts as valid, so the client cannot drift from the server's rules.
 */
export const useKeycodeValidation = (variantId: number): KeycodeValidationHook => {
	const [entries, setEntries] = useState<Record<string, TextInputEntry>>({});
	// A verdict is a point-in-time answer from the keying service and must not be replayed from cache.
	const [validateKeycode] = useLazyQuery<ValidateKeyCodeQuery, ValidateKeyCodeQueryVariables>(VALIDATE_KEYCODE, {
		fetchPolicy: 'no-cache'
	});
	// Most recent value requested per option id. useLazyQuery's promise cannot be cancelled, so a response
	// is only applied while it still matches - otherwise a newer keystroke has superseded it.
	const latestRequest = useRef<Record<string, string>>({});

	const handleSetTextInput = useCallback(
		(option: PricedOptionSelectionFieldsFragment, value: string) => {
			latestRequest.current[option.id] = value;

			if (!value) {
				setEntries((prev) => {
					const updated = { ...prev };
					delete updated[option.id];
					return updated;
				});
				return;
			}

			setEntries((prev) => ({ ...prev, [option.id]: { option, value, status: 'validating' } }));

			// A failed request leaves `isValid` false, so the entry settles as invalid.
			let isValid = false;
			validateKeycode({ variables: { keyCode: value, variantId } })
				.then(({ data }) => {
					isValid = data?.validateKeyCode.code === 'valid';
				})
				.catch((error) => logError(error))
				.finally(() => {
					if (latestRequest.current[option.id] !== value) return;
					setEntries((prev) => ({ ...prev, [option.id]: { option, value, status: isValid ? 'valid' : 'invalid' } }));
				});
		},
		[validateKeycode, variantId]
	);

	// validateKeyCode is variant-dependent, so switching variants must re-check every recorded value -
	// including rejected ones, since a code invalid for variant A may be valid for variant B. Keyed on
	// variantId alone: adding `entries` would re-fire on every verdict.
	useEffect(() => {
		Object.values(entries).forEach((entry) => handleSetTextInput(entry.option, entry.value));
	}, [variantId]);

	const textInputOptions = useMemo(
		() =>
			Object.values(entries)
				.filter((entry) => entry.status === 'valid')
				.map((entry) => ({ ...entry.option, value: entry.value })),
		[entries]
	);

	return { textInputs: entries, textInputOptions, handleSetTextInput };
};
